import { DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import type { DashboardDataResponse, DashboardWidgetKey, DashboardWidgetLayoutItem } from '@clair-avenir/shared';
import { Button } from '@ds/components/core/Button';
import { Icon } from '@ds/components/core/Icon';
import { AuthenticatedShell, Page, SectionHead } from '@/components/AppShell';
import { EmptyState } from '@/components/EmptyState';
import { useSession } from '@/features/auth/api';
import { useDashboardData, useDashboardLayout, useSaveDashboardLayout } from '@/features/dashboard/api';
import { BalanceWidget } from '@/features/dashboard/components/BalanceWidget';
import { DashboardWidgetShell } from '@/features/dashboard/components/DashboardWidgetShell';
import { EvolutionWidget } from '@/features/dashboard/components/EvolutionWidget';
import { InvoicesWidget } from '@/features/dashboard/components/InvoicesWidget';
import { ProjectedBalanceWidget } from '@/features/dashboard/components/ProjectedBalanceWidget';
import { SpendingByCategoryWidget } from '@/features/dashboard/components/SpendingByCategoryWidget';

export const Route = createFileRoute('/_authenticated/')({ component: DashboardPage });

const WIDGET_META: Record<DashboardWidgetKey, { title: string; description?: string }> = {
  saldo: { title: 'Saldo' },
  'gastos-categoria': { title: 'Por categoria' },
  evolucao: { title: 'Evolução', description: 'Últimos 6 meses' },
  faturas: { title: 'Faturas' },
  'projecao-saldo': { title: 'Saldo projetado' },
};

function renderWidgetContent(key: DashboardWidgetKey, data: DashboardDataResponse, onSelectCard: (cardId: string) => void) {
  switch (key) {
    case 'saldo':
      return <BalanceWidget balance={data.balance} />;
    case 'gastos-categoria':
      return <SpendingByCategoryWidget />;
    case 'evolucao':
      return <EvolutionWidget evolution={data.evolution} />;
    case 'faturas':
      return <InvoicesWidget invoices={data.invoices} onSelectCard={onSelectCard} />;
    case 'projecao-saldo':
      return <ProjectedBalanceWidget projection={data.projection} />;
  }
}

function DashboardPage() {
  const { data: user } = useSession();
  const navigate = useNavigate();
  const { data: dashboardData, isLoading: isDataLoading } = useDashboardData();
  const { data: layoutData, isLoading: isLayoutLoading } = useDashboardLayout();
  const saveLayout = useSaveDashboardLayout();

  const [layout, setLayout] = useState<DashboardWidgetLayoutItem[] | null>(null);
  const [initialized, setInitialized] = useState(false);
  // Semeia o estado local uma vez que o layout do servidor chega — a partir daí o
  // componente é a fonte da verdade (reordenar precisa de resposta imediata, sem esperar
  // o round-trip da mutação). Ajuste de estado durante o render, sem useEffect (React docs).
  if (layoutData && !initialized) {
    setLayout(layoutData);
    setInitialized(true);
  }

  const [editMode, setEditMode] = useState(false);
  const firstName = user?.name.split(' ')[0] ?? '';

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const orderedLayout = [...(layout ?? [])].sort((a, b) => a.position - b.position);
  const widgetIds = orderedLayout.map((w) => w.widgetKey);
  const visibleCount = orderedLayout.filter((w) => w.visible).length;

  function persist(next: DashboardWidgetLayoutItem[]) {
    setLayout(next);
    saveLayout.mutate(next);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedLayout.findIndex((w) => w.widgetKey === active.id);
    const newIndex = orderedLayout.findIndex((w) => w.widgetKey === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    persist(arrayMove(orderedLayout, oldIndex, newIndex).map((w, i) => ({ ...w, position: i })));
  }

  function toggleVisible(widgetKey: DashboardWidgetKey) {
    persist(orderedLayout.map((w) => (w.widgetKey === widgetKey ? { ...w, visible: !w.visible } : w)));
  }

  const isLoading = isDataLoading || isLayoutLoading || !dashboardData;

  return (
    <AuthenticatedShell title={`Olá, ${firstName}`}>
      <Page>
        <div data-testid="home-page">
          <SectionHead
            title="Visão geral"
            action={
              <Button
                size="sm"
                variant={editMode ? 'primary' : 'secondary'}
                iconLeft={<Icon name={editMode ? 'check' : 'sliders-horizontal'} />}
                data-testid="dashboard-customize-toggle"
                onClick={() => setEditMode((v) => !v)}
              >
                {editMode ? 'Concluir' : 'Personalizar'}
              </Button>
            }
          />

          {isLoading ? (
            <p style={{ font: 'var(--type-body)', color: 'var(--text-muted)', margin: 0 }}>Carregando...</p>
          ) : (
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              <SortableContext items={widgetIds} strategy={verticalListSortingStrategy}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gutter-section)' }}>
                  {!editMode && visibleCount === 0 ? (
                    <EmptyState
                      testId="dashboard-all-hidden"
                      icon="layout-grid"
                      title="Todos os widgets estão escondidos"
                      description="Clique em Personalizar pra reativar algum."
                    />
                  ) : (
                    orderedLayout.map((widget) => (
                      <DashboardWidgetShell
                        key={widget.widgetKey}
                        id={widget.widgetKey}
                        title={WIDGET_META[widget.widgetKey].title}
                        description={WIDGET_META[widget.widgetKey].description}
                        editMode={editMode}
                        visible={widget.visible}
                        onToggleVisible={() => toggleVisible(widget.widgetKey)}
                      >
                        {renderWidgetContent(widget.widgetKey, dashboardData, (cardId) => navigate({ to: '/cartoes/$cardId', params: { cardId } }))}
                      </DashboardWidgetShell>
                    ))
                  )}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </Page>
    </AuthenticatedShell>
  );
}
